/**
 * Type definitions for i18n language support.
 * This file defines the supported languages and provides type-safe utilities.
 */

/**
 * Supported languages in the application.
 * Currently supports English, Dutch, and French.
 */
export type Language = 'en' | 'nl' | 'fr';

/**
 * Array of all supported languages.
 * Use this for iterating over available languages.
 */
export const LANGUAGES: readonly Language[] = ['en', 'nl', 'fr'] as const;

/**
 * Display names for each supported language.
 * Keys are language codes, values are human-readable names.
 */
export const LANGUAGE_NAMES: Readonly<Record<Language, string>> = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
} as const;

/**
 * Type guard to check if a value is a valid Language.
 * Use this to safely validate user input or external data.
 *
 * @param value - The value to check
 * @returns True if the value is a valid Language, false otherwise
 *
 * @example
 * ```typescript
 * const userInput = 'en';
 * if (isLanguage(userInput)) {
 *   // TypeScript knows userInput is Language here
 *   changeLanguage(userInput);
 * }
 * ```
 */
export const isLanguage = (value: unknown): value is Language => {
  return typeof value === 'string' && LANGUAGES.includes(value as Language);
};
